import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EnvironmentsService, FileTransfersService, IOcbCUFileTransferRenamingParameter, IOcbFileTransfer, IOcbFileTransferRenamingParameter } from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxValidationGroupComponent } from 'devextreme-angular/ui/validation-group';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { forkJoin, from as fromPromise, Observable } from 'rxjs';
import { filter, finalize, mergeMap, tap } from 'rxjs/operators';
import { FileTransferPickerComponent } from '../../shared';

@Component({
    selector: 'app-file-transfers-renaming-parameters',
    templateUrl: './file-transfers-renaming-parameters.component.html',
    styleUrls: ['./file-transfers-renaming-parameters.component.scss'],
})
export class FileTransfersRenamingParametersComponent implements AfterViewInit {

    @ViewChild('renamingParameters')
    public rpGrid: DxDataGridComponent;
    @ViewChild('renamingParameterValidation')
    public rpValidationGroup: DxValidationGroupComponent;
    @ViewChild('fileTransferPicker')
    public fileTransferPicker: FileTransferPickerComponent;

    // Main Data-Grid
    public dataSource: IOcbFileTransferRenamingParameter[] = null;

    // List of file transfers
    public fileTransfers: IOcbFileTransfer[] = null;
    public selectedFileTransfer: IOcbFileTransfer = null;

    // Popup New & Edit
    public isPopupVisible: boolean = false;
    public selectedRenamingParameter: IOcbFileTransferRenamingParameter = {
        environmentId: '',
        fileTransferId: '',
        parameter: '',
    };
    public isNew: boolean = null;

    // Test
    public initialFileName: string = '';
    public renamedFileName: string = '';

    public isDropDownBoxOpened: boolean = false;
    public isLoadPanelVisible: boolean = true;
    public currentEnvironmentName: string = '';

    private currentEnvironmentId: string = '';
    private requestedFileTransferId?: string;

    constructor(
        private route: ActivatedRoute,
        private environmentsService: EnvironmentsService,
        private fileTransfersService: FileTransfersService,
        private translateService: TranslateService) {
        this.displayNewPopup = this.displayNewPopup.bind(this);
    }

    public ngAfterViewInit() {
        this.route.queryParams.subscribe(queryParams => {
            if (queryParams['fileTransfer']) this.requestedFileTransferId = queryParams['fileTransfer'];
        });
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
            this.environmentsService.getEnvironment(this.currentEnvironmentId).subscribe(env => {
                this.currentEnvironmentName = env.name;
            });
            this.loadFileTransfers();
        });
    }

    public onRowDblClick(e) { if (e?.data) this.displayEditPopup(e.data); }

    public displayNewPopup(parameter?: string): void {
        if (this.selectedFileTransfer) {
            this.selectedRenamingParameter = {
                environmentId: '',
                fileTransferId: '',
                parameter: parameter ?? '',
                rank: this.getNextRank(),
            };
            this.isNew = true;
            this.isPopupVisible = true;
        } else {
            this.translateService.get('_pickers.msgFileTransferRequired')
                .subscribe(message => {
                    notify(message, 'error', 3000);
                });
        }
    }

    public getNextRank(): number {
        let rank = 1;

        if (this.dataSource && this.dataSource.length > 0) {
            for (const element of this.dataSource) {
                if (element.rank >= rank) rank = element.rank + 1;
            }
        }

        return rank;
    }

    public displayEditPopup(renamingParameter: IOcbFileTransferRenamingParameter): void {
        this.selectedRenamingParameter = {
            environmentId: renamingParameter.id,
            fileTransferId: renamingParameter.fileTransferId,
            id: renamingParameter.id,
            parameter: renamingParameter.parameter,
            rank: renamingParameter.rank,
        };
        this.isNew = false;
        this.isPopupVisible = true;
    }

    public createRenamingParameter(): void {
        if (this.rpValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const renamingParameter: IOcbCUFileTransferRenamingParameter = {
                parameter: this.selectedRenamingParameter.parameter,
                rank: this.selectedRenamingParameter.rank,
            };
            this.fileTransfersService.addRenamingParameter(this.selectedFileTransfer.id, renamingParameter)
                .pipe(
                    mergeMap(_ => this.translateService.get('fileTransfersRenamingParameters.msgCreationComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe({
                    next: (message) => {
                        this.isPopupVisible = false;
                        this.loadDataSource();
                        notify(message, 'success', 3000);
                    },
                    error: (err) => this.handleError(err)
                });
        }
    }

    public updateRenamingParameter(): void {
        if (this.rpValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const renamingParameter: IOcbCUFileTransferRenamingParameter = {
                parameter: this.selectedRenamingParameter.parameter,
                rank: this.selectedRenamingParameter.rank,
            };
            this.fileTransfersService.updateRenamingParameter(this.selectedFileTransfer.id, this.selectedRenamingParameter.id, renamingParameter)
                .pipe(
                    mergeMap(_ => this.translateService.get('fileTransfersRenamingParameters.msgUpdateComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe({
                    next: (message) => {
                        this.isPopupVisible = false;
                        this.loadDataSource();
                        notify(message, 'success', 3000);
                    },
                    error: (err) => this.handleError(err)
                });
        }
    }

    public deleteRenamingParameter(id): void {
        forkJoin([
            this.translateService.get('fileTransfersRenamingParameters.msgDeleteConfirm'),
            this.translateService.get('fileTransfersRenamingParameters.msgDeleteConfirmTitle')
        ])
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the RESET request
                mergeMap(_ => this.fileTransfersService.deleteRenamingParameter(this.selectedFileTransfer.id, id)),
                // Reload the email template
                mergeMap(_ => this.translateService.get('fileTransfersRenamingParameters.msgDeleteComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                }, error: (err) => this.handleError(err)
            });
    }

    public testRenamingParameters(): void {
        if (this.selectedFileTransfer) {
            this.isLoadPanelVisible = true;
            this.fileTransfersService.testRenamingParameters(this.selectedFileTransfer.id, { value: this.initialFileName })
                .pipe(finalize(() => this.isLoadPanelVisible = false))
                .subscribe(message => this.renamedFileName = message);
        } else {
            this.translateService.get('_pickers.msgFileTransferRequired').subscribe(message => notify(message, 'error', 3000));
        }
    }

    public filterChanged(e): void {
        if (e) {
            this.selectedFileTransfer = e;
            this.isDropDownBoxOpened = false;
            this.loadDataSource();
        } else {
            this.dataSource = [];
        }
    }

    private loadFileTransfers(): void {
        this.currentEnvironmentId = this.route.snapshot.params.id;
        this.fileTransfersService.getFileTransfers(this.currentEnvironmentId, true)
            .pipe(
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (res) => {
                    this.fileTransfers = res;
                    if (this.requestedFileTransferId) this.fileTransferPicker.selectFileTransfer(this.requestedFileTransferId);
                },
                error: (err) => this.handleError(err)
            });
    }

    private loadDataSource(): void {
        if (this.selectedFileTransfer) {
            this.showLoading$('_commons.loading')
            .pipe(
                mergeMap(_ => this.fileTransfersService.getRenamingParameters(this.selectedFileTransfer.id)),
                finalize(() => this.rpGrid.instance.endCustomLoading()))
                .subscribe({
                    next: (res) => this.dataSource = res,
                    error: (err) => this.handleError(err)
                });
        } else {
            this.translateService.get('_pickers.msgFileTransferRequired')
                .subscribe(message => notify(message, 'error', 3000));
        }
    }

    private showLoading$(messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => this.rpGrid.instance.beginCustomLoading(`${message}...`)),
        );
    }

    private handleError(error: any): void {
        let message: string = '';
        if (error instanceof HttpErrorResponse) {
            if (error.error) {
                if (error.error.hasOwnProperty('message')) {
                    message = error.error.message;
                } else if (error.error.hasOwnProperty('name')) {
                    message = error.error.name;
                } else {
                    message = error.error;
                }
            } else {
                message = error.message;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }
}
